<template>
  <div
    class="idea-row select-list-item"
    :class="{ editable, expanded, [`${viewType}-view`]: true, completed: idea.isDone }"
    :data-idea-id="idea.id"
    @mouseenter="showOptions = true"
  >
    <div class="summary-wrapper d-flex">
      <div class="gripper gripper-icon" v-if="draggable && !isLocal"></div>
      <div class="summary row short-gutters flex-grow-1 flex-nowrap w-100">
        <div
          class="cell col title-col d-flex"
          :class="{ new: isLocal, 'show-idea-list': showIdeaList }"
        >
          <i
            class="caret caret-right-icon mr-2"
            :class="{ expanded: expanded }"
            @click="toggleExpansionAndSelect"
            v-if="canToggleExpansion"
          ></i>
          <div tabindex="-1" class="tab-pin"></div>
          <!-- 🖕This being used to  improve tab-key experience. While user is navigating through the list using up and down key,
           tab should take focus on checkbox of current row
           -->

          <input class="mx-1" type="checkbox" :checked="idea.isDone" @change="onStatusChange" />
          <div
            class="d-flex flex-column flex-grow-1 pointer input-wrapper"
            :class="{ 'inline-edit': editable }"
          >
            <input
              type="text"
              class="flex-grow-1 idea-title"
              v-model="ideaTitle"
              @keyup="lastKey = $event.keyCode"
              @keyup.enter="saveOnEnter"
              placeholder="Enter idea ([enter] to save, [esc] to cancel)"
              v-autofocus="editable"
              @blur="
                () => lastKey !== KEY_CODES.ENTER && lastKey !== KEY_CODES.ESCAPE && saveIdea()
              "
              @focus="editable = true"
              data-prevent-blur="true"
              v-if="editable"
            />
            <div
              class="idea-title-readview h-100 d-flex"
              :class="{ 'auto-show-action-icons': autoShowActionIcons }"
              @click="onTitleClick"
              v-else
            >
              <span class="idea-title-value wrap-hyphenate" :class="{ bold: expanded }">{{
                idea.title
              }}</span>
              <div class="icons-list align-self-baseline pl-2">
                <span
                  :data-comment-count="idea.commentCount"
                  class="comment-badge-icon"
                  title="comments"
                  @click.stop="onCommentBadgeClick"
                  :id="'idea-comments-' + idea.id"
                  v-if="!hideCommentIcon"
                >
                  <i class="comment-icon"></i>
                  <span v-if="idea.commentCount > 0">{{ idea.commentCount }}</span>
                </span>
                <span :id="'idea-edit-' + idea.id" class="edit-icon" @click.stop="editIdea"
                  ><i></i
                ></span>
                <span
                  :id="'idea-expander-' + idea.id"
                  class="expander-icon"
                  @click.stop="navigateToIdeaPage"
                  v-if="showDetailsPageNavigator"
                  ><i></i
                ></span>
                <action-tooltip
                  v-if="renderSecondLevelItems"
                  :target="'idea-comments-' + idea.id"
                  text="Comment"
                  :actions="viewType !== 'details' ? ['c'] : []"
                />
                <action-tooltip
                  v-if="renderSecondLevelItems"
                  :target="'idea-edit-' + idea.id"
                  text="Edit"
                  :actions="viewType !== 'details' ? ['e'] : []"
                />
                <action-tooltip
                  v-if="renderSecondLevelItems && showDetailsPageNavigator"
                  :target="'idea-expander-' + idea.id"
                  text="Open"
                  :actions="['Shift', rightKey]"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="idea-items">
          <div class="cell dropdown-cell text-center position-relative">
            <severity-dropdown @select="updateIdea({ reach: $event.id })" :selected="idea.reach" />
          </div>
          <div class="cell dropdown-cell text-center position-relative">
            <severity-dropdown
              @select="updateIdea({ effort: $event.id })"
              :selected="idea.effort"
            />
          </div>
          <div class="cell dropdown-cell text-center position-relative">
            <severity-dropdown
              @select="updateIdea({ impact: $event.id })"
              :selected="idea.impact"
            />
          </div>
          <div
            class="cell col-1 text-center border-right-0 owner pb-0 d-flex justify-content-center"
            tabindex="0"
            @click="toggleOwnerSelector"
            @keydown.enter.stop
            @keydown.space.prevent
            @keyup.space.enter="openOwnerSelector"
            @keydown.esc.down.up="closeOwnerSelector"
          >
            <ownership-short
              :entity="idea"
              :entity-type="ENTITY_TYPE.IDEA"
              :length="22"
              :showUserName="true"
              @change="onOwnershipChange"
            ></ownership-short>
          </div>
        </div>
        <div class="idea-options" v-if="!hideOptions">
          <idea-options
            v-if="showOptions"
            @open="navigateToIdeaPage"
            @edit="editIdea"
            @edit-note="editNote"
            :idea="idea"
            @delete-idea="$emit('delete-idea', $event)"
          />
        </div>
      </div>
    </div>
    <b-collapse v-model="expanded" class="note" v-if="renderSecondLevelItems">
      <div class="note-wrapper">
        <div class="note-section">
          <div class="sub-heading d-flex align-items-center">
            <i class="note-icon"></i><span class="ml-2">Note</span>
          </div>
          <template v-if="isDescriptionEditable">
            <div
              class="editor-wrapper d-flex flex-column"
              :class="{ 'errored-field': descriptionErrors && descriptionErrors.length }"
            >
              <ck-editor
                ref="descriptionEditor"
                v-if="usersInScope.length > 0"
                :config="descriptionEditorConfig"
                :id="'descriptionEditor-' + idea.id"
                :initial-data="idea.description"
                :initial-data-type="idea.descriptionType"
                @save="saveDescription"
              />
              <submit-buttons
                class="desc-submit-bar flex-grow-1 align-items-end"
                @save="saveDescription($refs.descriptionEditor.getData())"
                @cancel="cancelDescriptionEditing"
              />
            </div>
            <div
              class="field-error-message"
              v-if="descriptionErrors && descriptionErrors.length > 0"
            >
              {{ descriptionErrors.join('. ') }}
            </div>
          </template>
          <div class="d-flex align-items-baseline description" v-else-if="idea.description">
            <component
              class="w-100"
              :bg-type="viewType === 'details' ? 'default' : 'primary'"
              :is="expanded ? 'height-limiter' : 'div'"
              max-height="200"
              label="Show full note"
            >
              <p class="flex-grow-1" v-html="description"></p>
            </component>
            <span class="edit-icon" @click="isDescriptionEditable = true"><i></i></span>
          </div>
          <b-button
            variant="link"
            class="text-secondary text-medium add-note-btn"
            @click="isDescriptionEditable = true"
            v-else
            >Add a note
          </b-button>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import events from '@/constants/events'
import { ENTITY_TYPE } from '@/constants/entities'
import { IDEA_ROW } from '@/constants/idea'
import { ROUTE_NAMES } from '@/constants/routes'
import EventBusMixin from '@/mixins/EventBusMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import isLocalId from '@/utils/initiative/isLocalId'
import multiClickHandler from '@/utils/multiClickHandler'
import { getIdeaRowPageRouteConfig } from '@/utils/navigationConfigs'
import prettifyKey from '@/utils/prettifyKey'
import showAPIError from '@/utils/showAPIError'
import showModal from '@/utils/showModal'
import * as R from 'ramda'
import { BDropdownItem, BDropdown, BCollapse, BButton } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import OwnershipShort from '@/components/OwnershipShort'
import debounce from '@/utils/debounce'
import URLify from '@/utils/URLify'
import * as keymapService from '@/services/keymapService/keymapService'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { KEY_CODES } from '@/constants/keyCodes'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import CkEditor from '@/components/CkEditor'
import SubmitButtons from '@/components/SubmitButtons'
import IdeaModal from '@/components/ideas/IdeaModal'
import ActionTooltip from '@/components/ActionTooltip'
import IdeaOptions from '@/components/ideas/IdeaOptions'
import HeightLimiter from '@/components/HeightLimiter'
import SeverityDropdown from '@/components/ideas/SeverityDropdown'
import EventBus from '@/EventBus'
import expansionState from '@/expansionState'
import formatFreeText from '@/utils/formatFreeText'
import { getCKEditorConfig } from '@/ckeditorConfig'
import { MIME_TYPE, SEVERITY_MAP, SEVERITY_BADGE_MAP, DEBOUNCE_TIME } from '@/constants/common'
import editManager from '@/editManager'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import { trackEvent } from '@/analytics'

export default {
  name: IDEA_ROW,
  components: {
    BDropdownItem,
    BDropdown,
    BCollapse,
    OwnershipShort,
    CkEditor,
    SubmitButtons,
    BButton,
    ActionTooltip,
    IdeaOptions,
    HeightLimiter,
    SeverityDropdown
  },
  mixins: [MembershipsMixin, EventBusMixin, MediaQueryMixin],
  props: {
    idea: {},
    orgId: {},
    listId: {},
    draggable: { default: true },
    // list of all ideas. Needed for navigation to next/previous idea in idea modal
    ideas: { type: Array },
    showDetailsPageNavigator: {
      type: Boolean,
      default: true
    },
    canToggleExpansion: {
      type: Boolean,
      default: true
    },
    /*
    This tells where idea row is being used
    list - as list under IdeaList
    details - on idea page
    * */
    viewType: {
      default: 'list' // list | details
    },
    /**
     * this shows the idea in expanded mode on load
     * */
    expandOnLoad: Boolean,
    /**
     * Hide options menu
     */
    hideOptions: Boolean,
    hideCommentIcon: Boolean,
    /**
     * Action icons(that show up on hover) are shown upfront
     */
    autoShowActionIcons: Boolean
  },
  data() {
    return {
      editable: false, // tells if title is in edit mode
      isDescriptionEditable: false, // tells if description/note is in edit mode
      expanded:
        this.expandOnLoad ||
        !!(this.canToggleExpansion && expansionState.get('idea', this.idea.id)),
      lastKey: null,
      KEY_CODES,
      SEVERITY_BADGE_MAP,
      descriptionErrors: null, // contains errors occurred while editing description
      renderSecondLevelItems: false,
      showOptions: false,
      rightKey: prettifyKey('<right>')[0],
      ideaTitle: this.idea.title,
      impactOptions: [SEVERITY_MAP.low, SEVERITY_MAP.medium, SEVERITY_MAP.high],
      updatesBatch: {}, // when idea creation is under progress, further changes to ideas are stored here
      ROUTE_NAMES,
      ENTITY_TYPE
    }
  },
  computed: {
    ...mapGetters(['idea/ideaListById']),
    isLocal() {
      return isLocalId(this.idea.id)
    },
    description() {
      return formatFreeText(
        this.idea.organizationId,
        this.idea.description,
        this.idea.descriptionType
      )
    },
    ideaList() {
      return this.$store.getters['idea/ideaListById'](this.listId)
    },
    team() {
      const teamId = this.ideaList?.teamId
      return this.$store.getters.teamById(teamId)
    },
    usersInScope() {
      let users = []
      if (this.ideaList) {
        if (this.ideaList.type === 'team' && this.team.isPrivate) {
          users = this.$store.getters.teamMemberships(this.team.id).map(x => x.user)
        } else {
          users = this.$store.getters.orgMemberships(this.ideaList.organizationId).map(x => x.user)
        }
      }
      return users
    },
    descriptionEditorConfig() {
      return getCKEditorConfig({
        startupFocus: true,
        users: this.usersInScope,
        autoGrow: true,
        height: 215,
        orgId: this.idea.organizationId
      })
    }
  },
  methods: {
    ...mapActions('idea', ['getIdeaList', 'createIdea', 'patchIdea', 'deleteIdea']),
    editIdea() {
      setTimeout(() => {
        this.editable = true
      }, 0)
    },
    async saveIdea() {
      const { impact, ownerId, coOwnerIds, id } = this.idea
      try {
        if (this.isLocal) {
          if (!this.ideaTitle) {
            this.$emit('delete-idea', this.idea)
            return
          }
          const payload = {
            orgId: this.orgId,
            listId: this.listId,
            title: this.ideaTitle,
            impact,
            ownerId,
            coOwnerIds
          }
          let idea = await this.createIdea(payload)
          this.editable = false
          idea.localId = this.idea.id
          Object.assign(this.idea, idea)
          if (!R.isEmpty(this.updatesBatch)) {
            const updatedIdea = await this.updateIdea(this.updatesBatch)
            if (updatedIdea) {
              idea = updatedIdea
              this.updatesBatch = {}
            }
          }
          this.emitUpdateEvent(idea)
        } else {
          const payload = {
            orgId: this.orgId,
            id,
            data: {
              title: this.ideaTitle
            }
          }
          const idea = await this.patchIdea(payload)
          Object.assign(this.idea, idea)
          this.emitUpdateEvent(this.idea)
          this.editable = false
        }
      } catch (e) {
        const message =
          R.path(['response', 'data', 'detail', 'title', '0'], e) ||
          R.path(['response', 'data', 'message'], e) ||
          DEFAULT_ERROR
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    emitUpdateEvent(idea) {
      EventBus.$emit(events.IDEA_UPDATED, idea)
    },
    navigateToIdeaPage(event = {}, query = {}) {
      if (!this.editable) {
        const location = getIdeaRowPageRouteConfig({
          orgId: this.orgId,
          ideaId: this.idea.id,
          query
        })
        handleClickNavigation.call(this, event, location)
      }
    },
    changeStatus: debounce(async function(isDone) {
      if (isDone !== this.idea.isDone) {
        const payload = { orgId: this.orgId, id: this.idea.id, data: { isDone } }
        const idea = await this.patchIdea(payload)
        this.emitUpdateEvent(idea)
        this.$emit('change-status', isDone)
      }
    }, DEBOUNCE_TIME),
    onStatusChange: function(event) {
      const isDone = event.target.checked
      this.changeStatus(isDone)
    },
    URLify,
    cancelIdeaEdit() {
      // This function is being used for cancelling the editing on press of esc key
      this.editable = false
      this.ideaTitle = this.idea.title
      if (this.isLocal) {
        this.$emit('delete-idea', this.idea)
        this.selectLastElement()
      }
      this.lastKey = KEY_CODES.ESCAPE
      document.activeElement.blur()
    },
    selectLastElement() {
      const parentElement = this.$el.parentElement
      this.$nextTick(function() {
        const rows = parentElement.querySelectorAll('.select-list-item')
        const last = rows[rows.length - 1]
        last && last.classList.add('selected')
      })
    },
    selectCurrentIdea() {
      this.$el.classList.add('selected')
    },
    clearSelection() {
      const selectedItem = keymapService.selectedListItem(document)
      if (selectedItem) selectedItem.classList.remove('selected')
    },
    saveOnEnter() {
      this.saveIdea()
    },
    async updateIdea(data) {
      if (this.isLocal) {
        this.updatesBatch = { ...this.updatesBatch, ...data }
      } else {
        try {
          const payload = {
            orgId: this.orgId,
            id: this.idea.id,
            data
          }
          const idea = await this.patchIdea(payload)
          this.emitUpdateEvent({ ...this.idea, ...idea })
          return idea
        } catch (e) {
          showAPIError(e)
        }
      }
    },
    openOwnerSelector(event) {
      event.target.firstChild.__vue__.editing = true
    },
    closeOwnerSelector(event) {
      event.target.firstChild.__vue__.editing = false
    },
    toggleOwnerSelector(event) {
      const isEditing = event.currentTarget.firstChild.__vue__.editing
      event.currentTarget.firstChild.__vue__.editing = !isEditing
    },
    toggleExpansionAndSelect({ forceExpand = false } = {}) {
      if (forceExpand) {
        this.expanded = true
      } else if (this.canToggleExpansion) {
        this.expanded = !this.expanded
      }
      this.$nextTick(function() {
        this.clearSelection()
        this.selectCurrentIdea()
      })
    },
    editNote() {
      this.toggleExpansionAndSelect({ forceExpand: true })
      this.isDescriptionEditable = true
    },
    onTitleClick() {
      multiClickHandler({
        onSingleClick: () => {
          this.isDesktopMode ? this.toggleExpansionAndSelect() : this.navigateToIdeaPage()
        },
        onDoubleClick: () => {
          this.editable = true
        }
      })
    },
    async saveDescription(description) {
      try {
        const payload = {
          orgId: this.orgId,
          id: this.idea.id,
          data: { description, descriptionType: MIME_TYPE.HTML }
        }
        const idea = await this.patchIdea(payload)
        this.emitUpdateEvent({ ...this.idea, ...idea })
        this.edit = false
        this.isDescriptionEditable = false
        this.descriptionErrors = null
      } catch (e) {
        let errors = R.path(['response', 'data', 'detail', 'description'], e)
        if (!errors) {
          const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
          errors = [message]
        }
        this.descriptionErrors = errors
      }
    },
    onCommentAdded({ value: idea, type }) {
      if (type === 'idea' && idea.id === this.idea.id) {
        const currentCount = this.idea.commentCount || 0
        this.emitUpdateEvent({ ...this.idea, commentCount: currentCount + 1 })
      }
    },
    onCommentDeleted({ value: idea, type }) {
      if (type === 'idea' && idea.id === this.idea.id) {
        this.emitUpdateEvent({
          ...this.idea,
          commentCount: this.idea.commentCount - 1
        })
      }
    },
    onCommentBadgeClick() {
      this.showActivityModal()
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.ACTIVITY_MODAL, EVENT_NAME.IDEA)
    },
    showActivityModal(focusAddCommentOnLoad) {
      showModal({
        component: IdeaModal,
        id: `${ENTITY_TYPE.IDEA}-${this.idea.id}`,
        props: {
          show: true,
          ideas: this.ideas,
          idea: this.idea,
          focusAddCommentOnLoad
        }
      })
    },
    cancelDescriptionEditing() {
      this.isDescriptionEditable = false
      this.descriptionErrors = null
    },
    onIdeaUpdate(idea) {
      if (idea.id === this.idea.id) {
        this.$emit('update-idea', idea)
      }
    },
    onOwnershipChange(updatedIdea) {
      this.emitUpdateEvent({ ...this.idea, ...updatedIdea })
    }
  },
  mounted() {
    if (this.isLocal) {
      setTimeout(() => (this.editable = true), 0)
    }
    if (this.expanded) {
      this.renderSecondLevelItems = true
    } else {
      setTimeout(() => (this.renderSecondLevelItems = true), 500)
    }
    this.listenToEventBus(events.COMMENT_ADDED, this.onCommentAdded)
    this.listenToEventBus(events.COMMENT_DELETED, this.onCommentDeleted)
    this.listenToEventBus(events.IDEA_UPDATED, this.onIdeaUpdate)
  },
  watch: {
    editable() {
      this.$nextTick(function() {
        this.clearSelection() // This will unselect the previously selected item
        this.selectCurrentIdea()
      })
      if (this.editable) {
        this.lastKey = null
      }
    },
    expanded(isExpanded) {
      expansionState.set('idea', this.idea.id, isExpanded)
    },
    async isDescriptionEditable(edit) {
      if (edit) {
        if (!this.ideaList) {
          await this.getIdeaList({
            orgId: this.idea.organizationId,
            listId: this.listId
          })
        }
        this.descriptionEditId = editManager.enterEditMode()
        if (this.usersInScope.length === 0) {
          this.updateMemberships(this.orgId, this.team.id, this.team.isPrivate)
        }
      } else {
        this.descriptionEditId = editManager.exitEditMode(this.descriptionEditId)
      }
    }
  },
  beforeDestroy() {
    this.descriptionEditId && editManager.exitEditMode(this.descriptionEditId)
  }
}
</script>

<style lang="scss" scoped>
@import './IdeaRow';
</style>
